import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import { useContent, useCompany } from "@/lib/content-context";
import { api, formatApiError } from "@/lib/api";

const COUNT_OPTIONS = ["Ne 6 – 10", "Ne 12 – 20", "Ne 21 – 30", "Ne 32 – 40", "Ne 42 – 50", "Multi-fold (2-ply / 3-ply)", "Not sure yet"];

const initial = { name: "", company: "", email: "", phone: "", yarn_type: "", count_range: "", quantity: "", message: "" };

export default function Contact() {
  usePageTitle("Contact");
  const { content } = useContent();
  const c = content.contact;
  const company = useCompany();
  const yarnTypes = content.products.yarn_types || [];

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      await api.post(`/inquiries`, payload);
      toast.success("Thanks — your inquiry has been received. Our team will respond shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-6">{c.hero_overline}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            {c.hero_heading}
          </h1>
          <p className="mt-6 text-[#5C5A56] text-lg max-w-2xl">{c.hero_subtitle}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <form data-testid="inquiry-form" onSubmit={onSubmit} className="bg-[#F8F7F4] border border-[#1A1A1A]/10 p-8 md:p-12">
              <div className="overline mb-2">{c.form_overline}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl mb-10">{c.form_heading}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                <Field label="Your name *">
                  <input data-testid="inquiry-name" required value={form.name} onChange={update("name")} className="input-editorial" placeholder="Full name" />
                </Field>
                <Field label="Company">
                  <input data-testid="inquiry-company" value={form.company} onChange={update("company")} className="input-editorial" placeholder="Company / Mill" />
                </Field>
                <Field label="Email *">
                  <input data-testid="inquiry-email" required type="email" value={form.email} onChange={update("email")} className="input-editorial" placeholder="you@company.com" />
                </Field>
                <Field label="Phone">
                  <input data-testid="inquiry-phone" value={form.phone} onChange={update("phone")} className="input-editorial" placeholder="+91 ..." />
                </Field>
                <Field label="Yarn type">
                  <select data-testid="yarn-type-select" value={form.yarn_type} onChange={update("yarn_type")} className="input-editorial bg-transparent">
                    <option value="">Select yarn type</option>
                    {yarnTypes.map((y, i) => (
                      <option key={i} value={y.short ? `${y.name} (${y.short})` : y.name}>
                        {y.short ? `${y.name} (${y.short})` : y.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Count range">
                  <select data-testid="count-select" value={form.count_range} onChange={update("count_range")} className="input-editorial bg-transparent">
                    <option value="">Select count range</option>
                    {COUNT_OPTIONS.map(co => <option key={co} value={co}>{co}</option>)}
                  </select>
                </Field>
                <Field label="Indicative quantity" full>
                  <input data-testid="inquiry-quantity" value={form.quantity} onChange={update("quantity")} className="input-editorial" placeholder="e.g. 5,000 kg / month" />
                </Field>
                <Field label="Message *" full>
                  <textarea data-testid="inquiry-message" required value={form.message} onChange={update("message")} className="textarea-editorial" placeholder="Tell us about your construction, end-use, certifications required…" />
                </Field>
              </div>

              <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-xs text-[#5C5A56]">{c.form_disclaimer}</div>
                <button type="submit" data-testid="inquiry-submit-button" disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Inquiry <ArrowUpRight size={16} /></>}
                </button>
              </div>

              {submitted && (
                <div data-testid="inquiry-success" className="mt-8 p-5 bg-[#2E4A3D] text-[#F8F7F4]">
                  <div className="overline text-[#C25934] mb-2">Inquiry received</div>
                  <div className="text-sm">Thank you. We&apos;ve recorded your request and our team will respond shortly.</div>
                </div>
              )}
            </form>
          </div>

          {/* CONTACT INFO */}
          <aside className="lg:col-span-5 space-y-8">
            <div className="bg-[#1A1A1A] text-[#F8F7F4] p-8 md:p-10">
              <div className="overline text-[#C25934] mb-3">Reach Us</div>
              <h3 className="font-serif-display text-3xl">{company.name}</h3>
              <div className="font-mono-ed text-[10px] tracking-widest text-[#D9D4CC]/70 mt-2">CIN · {company.cin}</div>

              <div className="divider-line bg-white/15 my-7" />

              <div className="space-y-5 text-sm">
                <div className="flex gap-3">
                  <MapPin size={18} className="text-[#C25934] shrink-0 mt-0.5" />
                  <span data-testid="contact-address" className="text-[#D9D4CC] leading-relaxed">
                    {company.addressLines.map((l, i) => <span key={i}>{l}<br/></span>)}
                  </span>
                </div>
                <div className="flex gap-3 items-center">
                  <Phone size={18} className="text-[#C25934]" />
                  <a data-testid="contact-phone" href={`tel:${company.phoneRaw}`} className="link-underline text-[#D9D4CC]">{company.phone}</a>
                </div>
                <div className="flex gap-3 items-center">
                  <Mail size={18} className="text-[#C25934]" />
                  <a data-testid="contact-email" href={`mailto:${company.email}`} className="link-underline text-[#D9D4CC]">{company.email}</a>
                </div>
              </div>
            </div>

            <div className="bg-[#EAE6DF] p-8 md:p-10">
              <div className="overline mb-3">{c.business_hours_overline}</div>
              <h4 className="font-serif-display text-2xl mb-4">{c.business_hours_heading}</h4>
              <div className="space-y-2 text-sm text-[#5C5A56]">
                <div className="flex justify-between">
                  <span>{company.businessHours.weekdayLabel}</span>
                  <span className="font-mono-ed">{company.businessHours.weekdayValue}</span>
                </div>
                <div className="flex justify-between">
                  <span>{company.businessHours.sundayLabel}</span>
                  <span className="font-mono-ed">{company.businessHours.sundayValue}</span>
                </div>
              </div>
              <div className="divider-line my-6" />
              <div className="text-xs text-[#5C5A56]">{c.business_hours_note}</div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5A56] mb-2 font-semibold">{label}</label>
      {children}
    </div>
  );
}
